import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AngularFireRemoteConfig } from '@angular/fire/compat/remote-config';
import { filter, fromEvent, Subscription, tap } from 'rxjs';
import { HttpCancelService } from './services/http-cancel.service';
import { DynamicScriptLoaderService } from './services/dynamic-script-loader.service';
import { environment } from 'src/environments/environment';
import { cacheKeys } from './constants/cache-keys';
import { Script } from './models/script.model';
import { faqs } from './constants/faqs';
import { NotifyDialogComponent } from './components/utility/notify-dialog/notify-dialog.component';
import { CallAPIService } from './services/call-api.service';
import { StoreService } from './services/store.service';
import { CachingService } from './services/caching.service';
import { UtilityService } from './services/utility.service';
import { TollFreeComponent } from './components/utility/toll-free/toll-free.component';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { JSONLDService } from './services/json-ld.service';
import { SeoService } from './services/seo.service';
import { UtilsService } from './services/utils.service';
import { QuestionnaireComponent } from './components/utility/questionnaire-dialog/questionnaire.component';

declare const gtag: Function;
declare const toggleDarkMode:any;
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('drawerState', [
      state('closed', style({
        transform: 'translateX(100%)'
      })),
      state('open', style({
        transform: 'translateX(0)'
      })),
      transition('closed <=> open', [
        animate('300ms ease-in-out')
      ])
    ])
  ]
})
export class AppComponent implements OnInit, OnDestroy  {
  schema!: any;
  title = 'landing';
  headFootVisible = true;
  public showDownloadApp:any;
  private schemaChangeSubs!: Subscription;
  private queryParamsSubs?: Subscription;
  private routeChangeSubs!: Subscription;
  private botScriptSubs!: Subscription;
  private userCountSubs?:Subscription;
  drawerOpen: boolean = false;
  private packageJSON = require('../../package.json');
  private faqsList = faqs;
  importantUpdates: any[] = [];
  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private matDialog: MatDialog,
    private renderer: Renderer2,
    private fireRemoteConfig: AngularFireRemoteConfig,
    private changeDetectRef: ChangeDetectorRef,
    public jsonLDService: JSONLDService,
    private httpCancelService: HttpCancelService,
    private dynamicScriptLoadService: DynamicScriptLoaderService,
    private callApiService: CallAPIService,
    private storeService: StoreService,
    private cacheStorage: CachingService,
    private utilService:UtilityService,
    private seoService: SeoService,
    private utilityServicE: UtilsService
  ) {}


  ngOnInit() {
    this.logBuildVersion();
    this.addSchemaListener();
    this.initRouteChangeListener();
    this.fetchDialogRemoteConfig();
    this.fetchNotifyDialogRemoteConfig();
    this.fetchTollFreeDialogRemoteConfig();
    this.generateJsonLDFaqs();
    this.checkForUserAgent();
    if(localStorage.getItem('questionnaireDialogOpened') !== 'true'){
      this.openQuestionnaire();
    }
  }

  openQuestionnaire() {
    this.matDialog.open(QuestionnaireComponent, {
      disableClose: true,
      autoFocus: false
    });
  }

  toggleDrawer() {
    this.drawerOpen = !this.drawerOpen;
  }

  getImportantUpdates(event:any) {
    this.importantUpdates = event;
  }

  checkForUserAgent(){
    if (isPlatformServer(this.platformId)) {
      return;
    }
    const {deviceOS} = this.utilService.getDeviceOS();
    this.showDownloadApp = this.utilService.isDeviceAndroid(deviceOS) || this.utilService.isDeviceIOS(deviceOS);
  }
  
  private logBuildVersion() {
    console.log(
      `%c${this.packageJSON.version}`,
      'color:orange;font-size:20px;font-weight:500;'
    );
  }

  private addSchemaListener() {
    this.schemaChangeSubs = this.jsonLDService.jsonSchema$.subscribe(
      (schema) => {
        this.schema = schema;
        this.seoService.appendOrUpdateJsonLd(schema)
        this.changeDetectRef.detectChanges();
      }
    );
  }

  private generateJsonLDFaqs() {
    const faqs = this.faqsList;
    this.jsonLDService.jsonSchema$.next(faqs);
  }

  private initRouteChangeListener() {
    let currentUrl: string;
    this.routeChangeSubs = this.router.events
      .pipe(
        tap((event) => {
          if (event instanceof NavigationStart) {
            currentUrl = event.url;
          }
        }),
        filter((event) => event instanceof NavigationEnd),
        tap((event) => {
          const { urlAfterRedirects } = <NavigationEnd>event;

          this.headFootVisible =
            !urlAfterRedirects.endsWith('/dashboard/shared');
        }),
        filter(() => !isPlatformServer(this.platformId))
      )
      .subscribe((event) => {
        const { urlAfterRedirects } = <NavigationEnd>event;

        if (currentUrl !== urlAfterRedirects) {
          this.httpCancelService.cancelAllRequests();
        }

        gtag('config', 'UA-87973874-4', { page_path: urlAfterRedirects });
        gtag('event', 'open', {
          event_category: 'UMANG-LANDING',
          event_label: urlAfterRedirects,
        });
        this.getUserCountsApi();
      });
  }



  fetchTollFreeDialogRemoteConfig() {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    
    const remoteConfigKey = 'web_toll_free_config';

      this.fireRemoteConfig.fetchAndActivate().then(() => {
        this.fireRemoteConfig.getValue(remoteConfigKey).then((config) => {
          try {
            const parsedConfig = JSON.parse(config.asString());
            // cacheTimeout=10mins
            const toStoreConfig = {
              config: parsedConfig['landing_toll_free_config_prod'],
            };
            this.openTollFreeDialog(toStoreConfig.config);
          } catch (error: any) {}
        });
      });

  }

  private openTollFreeDialog(config: any) {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    if (!config.isActive) {
      return;
    }

    const localStroeTimeStamp:any = localStorage.getItem('toll-free-timestamp-landing');
    if(config?.isActive) {
      if(+localStroeTimeStamp != config?.timeStamp){
        this.matDialog.open(TollFreeComponent, { data: config });
      }
    }

  }


  private fetchNotifyDialogRemoteConfig() {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const remoteConfigKey = 'web_pre_login_notify';

    // Fetching config from firebase if not present in localStorage
    const fetchConfigEssentials = () => {
      this.fireRemoteConfig.fetchAndActivate().then(() => {
        this.fireRemoteConfig.getValue(remoteConfigKey).then((config) => {
          try {
            const parsedConfig = JSON.parse(config.asString());
            // cacheTimeout=10mins
            const cacheTimeout = new Date().getTime() + 0.2 * 60 * 1000;
            const toStoreConfig = {
              config: parsedConfig['web_pre_login_notify'],
              cacheTimeout,
            };
            localStorage.setItem(
              cacheKeys.remoteConfig,
              JSON.stringify(toStoreConfig)
            );

            this.openNotifyDialog(toStoreConfig.config);
          } catch (error: any) {}
        });
      });
    };

    try {
      const { cacheTimeout, config } = JSON.parse(
        localStorage.getItem(cacheKeys.remoteConfig)!
      );
      // Checking if cache is invalid or not
      if (cacheTimeout > new Date().getTime()) {
        this.openNotifyDialog(config);
        return;
      }
      throw new Error('No valid cached config found for welcome dialog.');
    } catch (error: any) {
      console.log('Error in fetching cached remote config:', error?.message);
      localStorage.removeItem(cacheKeys.remoteConfig);
      fetchConfigEssentials();
    }
  }

  private openNotifyDialog(config: any) {
    if (!config.isActive) {
      return;
    }

    this.matDialog.open(NotifyDialogComponent, { data: config });
  }
  private fetchDialogRemoteConfig() {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const remoteConfigKey = 'web_landing_popup';

    // Fetching config from firebase if not present in localStorage
    const fetchConfigEssentials = () => {
      this.fireRemoteConfig.fetchAndActivate().then(() => {
        this.fireRemoteConfig.getValue(remoteConfigKey).then((config) => {
          try {
            const parsedConfig = JSON.parse(config.asString());
            // cacheTimeout=10mins
            const cacheTimeout = new Date().getTime() + 0.2 * 60 * 1000;
            const toStoreConfig = {
              config:
                parsedConfig[
                  environment.production
                    ? 'web_landing_config_prod'
                    : 'web_landing_config_stg'
                ],
              cacheTimeout,
            };
            localStorage.setItem(
              cacheKeys.remoteConfig,
              JSON.stringify(toStoreConfig)
            );

            // this.openWelcomeDialog(toStoreConfig.config);
          } catch (error: any) {
            console.log('Error in fetching remote config: ', error?.message);
          }
        });
      });
    };

    try {
      const { cacheTimeout, config } = JSON.parse(
        localStorage.getItem(cacheKeys.remoteConfig)!
      );
      // Checking if cache is invalid or not
      if (cacheTimeout > new Date().getTime()) {
        this.openWelcomeDialog(config);
        return;
      }
      throw new Error('No valid cached config found for welcome dialog.');
    } catch (error: any) {
      console.log('Error in fetching cached remote config:', error?.message);
      localStorage.removeItem(cacheKeys.remoteConfig);
      fetchConfigEssentials();
    }
  }

  private openWelcomeDialog(config: any) {
    if (!config.isActive) {
      return;
    }

    // this.matDialog.open(WelcomeDialogComponent, { data: config });
  }

  private loadChatbotScripts() {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const chatbotUrl = environment.production
      ? 'https://s3-umangconvai-prod-chatbot.s3.ap-south-1.amazonaws.com/UMANG/SenseforthChatSDKLoader.js'
      : 'https://uat.ai.umangapp.in/GenericChatBot/UMANG/SenseforthChatSDKLoader.js';

    const chatbotScripts: Script[] = [
      {
        src: chatbotUrl,
        defer: true,
      },
      { src: 'scripts/chat-bot.js', defer: true },
    ];

    const loadScriptsEssentials = () => {
      this.dynamicScriptLoadService.loadScripts(this.renderer, chatbotScripts);
    };

    // If application already loaded with 'complete' state
    if (this.document.readyState === 'complete') {
      return loadScriptsEssentials();
    }

    // Else we wait for the state to be 'complete'
    this.botScriptSubs = fromEvent(this.document, 'readystatechange')
      .pipe(
        filter((event) => (<Document>event.target).readyState === 'complete')
      )
      .subscribe(() => {
        loadScriptsEssentials();
      });
  }


  getUserCountsApi(){
    if(this.cacheStorage.fetchCachedData(cacheKeys.userCounts)){
      return;
    }


    this.userCountSubs= this.callApiService.getUserCountsData().subscribe({
      next:(res:any) => {
        this.cacheStorage.storeCachedData({
          key: cacheKeys.userCounts,
          data: res,
        });
        this.storeService.updateVisitCount.next(true);
      }
    })
  }

  ngOnDestroy() {
    if(isPlatformServer(this.platformId)){
      return
    }
    this.schemaChangeSubs.unsubscribe();
    this.routeChangeSubs.unsubscribe();
    this.botScriptSubs?.unsubscribe();
    this.queryParamsSubs?.unsubscribe();
    this.userCountSubs?.unsubscribe();
  }
}
